import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Activity } from "lucide-react";
import api from "../services/api.js";

import PppMenu from "../components/Customer/Ppp/PppMenu.js";
import SyncPppActiveConByUsermanId from "../components/Customer/Ppp/SyncPppActiveConByUsermanId.js";

const PppActiveCon = () => {
  const [searchParams] = useSearchParams();

  const usermanId = searchParams.get("userman_id");
  const deviceName = searchParams.get("device_name");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const limit = 1000;

  const [totalPages, setTotalPages] = useState(1);
  const [totalBackend, setTotalBackend] = useState(0);

  const [filters, setFilters] = useState({
    name: "",
    customer_id: "",
    bill_status: "",
    service: "",
    address: "",
  });

  const [quickSearch, setQuickSearch] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");

      const res = await api.get("https://api.wifikamu.my.id/ppp-active-cons", {
        headers: {
          Authorization: token,
        },
        params: {
          page,
          limit,
          userman_id: usermanId,
          name: filters.name || undefined,
          customer_id: filters.customer_id || undefined,
          bill_status: filters.bill_status || undefined,
          service: filters.service || undefined,
          address: filters.address || undefined,
        },
      });

      setData(res.data.data || []);
      setTotalBackend(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error(err);
      setError("Gagal mengambil data PPP Active");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (usermanId) {
      fetchData();
    }
  }, [usermanId, page]);

  const handleSearch = () => {
    setPage(1);
    fetchData();
  };

  const filteredData = data.filter((item) =>
    [item.name, item.address, item.customer_id, item.session_id]
      .join(" ")
      .toLowerCase()
      .includes(quickSearch.toLowerCase())
  );

  return (
    <div className="p-6 dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-200">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Activity className="w-6 h-6" />
          {deviceName} /{" "}
          <PppMenu userman_id={usermanId} device_name={deviceName} /> / Active
          Connections
        </h1>

        <div className="flex items-center gap-2">
          <SyncPppActiveConByUsermanId
            usermanId={usermanId}
            onSuccess={() => {
              fetchData();
            }}
          />
        </div>
      </div>

      {/* FILTER */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-3">
        <input
          type="text"
          placeholder="Name"
          value={filters.name}
          onChange={(e) =>
            setFilters({
              ...filters,
              name: e.target.value,
            })
          }
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <input
          type="text"
          placeholder="Customer ID"
          value={filters.customer_id}
          onChange={(e) =>
            setFilters({
              ...filters,
              customer_id: e.target.value,
            })
          }
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <input
          type="text"
          placeholder="Address"
          value={filters.address}
          onChange={(e) =>
            setFilters({
              ...filters,
              address: e.target.value,
            })
          }
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <select
          value={filters.bill_status}
          onChange={(e) =>
            setFilters({
              ...filters,
              bill_status: e.target.value,
            })
          }
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        >
          <option value="">All Bill Status</option>
          <option value="paid">paid</option>
          <option value="unpaid">unpaid</option>
          <option value="not found">not found</option>
        </select>

        <button
          onClick={handleSearch}
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Search
        </button>
      </div>

      {/* QUICK SEARCH */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <input
          type="text"
          placeholder="Quick search"
          value={quickSearch}
          onChange={(e) => setQuickSearch(e.target.value)}
          className="w-full max-w-sm px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <div className="flex gap-4 text-sm">
          <span>
            Total Active: <b>{totalBackend}</b>
          </span>

          <span>
            Showing: <b>{filteredData.length}</b>
          </span>
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto rounded border dark:border-slate-700">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Customer ID</th>
              <th className="px-4 py-2 text-left">Address</th>
              <th className="px-4 py-2 text-left">Service</th>
              <th className="px-4 py-2 text-left">Session ID</th>
              <th className="px-4 py-2 text-left">Uptime</th>
              <th className="px-4 py-2 text-center">Bill</th>
              <th className="px-4 py-2 text-center">Radius</th>
              <th className="px-4 py-2 text-center">Updated</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.map((row) => (
              <tr key={row.id} className="border-t dark:border-slate-700">
                <td className="px-4 py-2">{row.name}</td>
                <td className="px-4 py-2">{row.customer_id || "-"}</td>
                <td className="px-4 py-2">{row.address || "-"}</td>
                <td className="px-4 py-2">{row.service || "-"}</td>
                <td className="px-4 py-2">{row.session_id || "-"}</td>
                <td className="px-4 py-2">{row.uptime || "-"}</td>

                <td className="px-4 py-2 text-center">
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      row.bill_status === "paid"
                        ? "bg-green-100 text-green-700"
                        : row.bill_status === "unpaid"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {row.bill_status}
                  </span>
                </td>

                <td className="px-4 py-2 text-center">
                  {row.radius ? (
                    <span className="text-green-500">Yes</span>
                  ) : (
                    <span className="text-red-500">No</span>
                  )}
                </td>

                <td className="px-4 py-2 text-center">
                  {new Date(row.updated_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="mt-4 flex items-center gap-2">
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-3 py-1 rounded border"
        >
          Prev
        </button>

        <span>
          Page {page} / {totalPages}
        </span>

        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1 rounded border"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PppActiveCon;
